import React from 'react'
import { useNavigate } from 'react-router-dom'
import { User, Car, DollarSign, Calendar, Edit, Trash2, Eye, CheckCircle, XCircle, Clock, AlertCircle } from 'lucide-react'
import { Proposal } from '../../../types'
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../ui'
import { Button } from '../../ui/Button'
import { formatCurrency } from '../../../utils/formatters'

interface ProposalsListViewProps {
 proposals: Proposal[]
 onViewProposal?: (proposal: Proposal) => void
 onEditProposal: (proposal: Proposal) => void
 onDeleteProposal: (proposalId: string) => void
 onAddProposal: () => void
 searchTerm?: string
 statusFilter?: string
}

export const ProposalsListView: React.FC<ProposalsListViewProps> = ({
 proposals,
 onViewProposal,
 onEditProposal,
 onDeleteProposal,
 onAddProposal,
 searchTerm,
 statusFilter
}) => {
 const navigate = useNavigate()
 const getStatusIcon = (status: Proposal['status'] | string) => {
 switch (status) {
 case 'draft':
 return <Edit className="h-4 w-4" />
 case 'sent':
 return <Clock className="h-4 w-4" />
 case 'negotiating':
 return <AlertCircle className="h-4 w-4" />
 case 'accepted':
 case 'approved':
 return <CheckCircle className="h-4 w-4" />
 case 'rejected':
 return <XCircle className="h-4 w-4" />
 case 'expired':
 return <Clock className="h-4 w-4" />
 case 'cancelled':
 return <XCircle className="h-4 w-4" />
 default:
 return <Clock className="h-4 w-4" />
 }
 }

 const getStatusBadgeClass = (status: Proposal['status'] | string) => {
 switch (status) {
 case 'draft':
 return 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300'
 case 'sent':
 return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
 case 'negotiating':
 return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
 case 'accepted':
 case 'approved':
 return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
 case 'rejected':
 case 'cancelled':
 return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
 case 'expired':
 return 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400'
 default:
 return 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400'
 }
 }

 // kept for icon coloring
 const getStatusColor = (status: Proposal['status'] | string) => {
 switch (status) {
 case 'draft': return 'text-slate-500'
 case 'sent': return 'text-blue-600'
 case 'negotiating':return 'text-amber-600'
 case 'accepted':
 case 'approved': return 'text-emerald-600'
 case 'rejected':
 case 'cancelled': return 'text-red-600'
 case 'expired': return 'text-slate-400'
 default: return 'text-slate-500'
 }
 }

 const getStatusLabel = (status: Proposal['status'] | string) => {
 const labels: Record<string, string> = {
 draft: 'Rascunho',
 sent: 'Enviada',
 negotiating: 'Negociando',
 accepted: 'Aceita',
 approved: 'Aprovada',
 rejected: 'Rejeitada',
 expired: 'Expirada',
 cancelled: 'Cancelada'
 }
 return labels[status] || status
 }

 if (proposals.length === 0) {
 return (
 <div className="text-center py-12">
 <Car className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
 <p className="text-muted-foreground">
 {searchTerm || (statusFilter && statusFilter !== 'all')
 ? 'Nenhuma proposta encontrada com os filtros aplicados'
 : 'Nenhuma proposta cadastrada'}
 </p>
 {!searchTerm && (!statusFilter || statusFilter === 'all') && (
 <Button className="mt-4" onClick={onAddProposal}>
 <Car className="h-4 w-4 mr-2" />
 Criar Primeira Proposta
 </Button>
 )}
 </div>
 )
 }

 return (
 <Table>
 <TableHeader>
 <TableRow>
 <TableHead>Cliente</TableHead>
 <TableHead>Veículo</TableHead>
 <TableHead>Proposta</TableHead>
 <TableHead>Status</TableHead>
 <TableHead>Vendedor</TableHead>
 <TableHead>Data</TableHead>
 <TableHead>Ações</TableHead>
 </TableRow>
 </TableHeader>
 <TableBody>
 {proposals.map((proposal) => (
 <TableRow 
 key={proposal.id} 
 hover 
 onClick={() => onViewProposal?.(proposal)}
 className="cursor-pointer"
 >
 <TableCell>
 <div className="flex items-center">
 <div className="h-10 w-10 bg-accent/10 dark:bg-primary-900 rounded-full flex items-center justify-center mr-4">
 <User className="h-5 w-5 text-accent dark:text-accent" />
 </div>
 <div>
 <p className="font-medium text-foreground">
 {proposal.lead.name}
 </p>
 <div className="text-sm text-muted-foreground space-y-1">
 {proposal.lead.email && <p>{proposal.lead.email}</p>}
 {proposal.lead.phone && <p>{proposal.lead.phone}</p>}
 </div>
 </div>
 </div>
 </TableCell>
 
 <TableCell>
 <div className="flex items-center">
 {proposal.vehicle.photos && proposal.vehicle.photos[0] && (
 <img
 src={proposal.vehicle.photos[0].url}
 alt={proposal.vehicle.name}
 className="w-12 h-12 object-cover rounded-lg mr-3 flex-shrink-0"
 />
 )}
 <div>
 <p className="font-medium text-foreground">
 {(typeof proposal.vehicle.brand === 'object' ? (proposal.vehicle.brand as any)?.name : proposal.vehicle.brand) || ''} {(typeof proposal.vehicle.model === 'object' ? (proposal.vehicle.model as any)?.name : proposal.vehicle.model) || ''}
 </p>
 <p className="text-sm text-muted-foreground">
 {(proposal.vehicle as any).year || (proposal.vehicle as any).modelYear}
 </p>
 </div>
 </div>
 </TableCell>
 
 <TableCell>
 <div className="space-y-1">
 <div className="flex items-center gap-2">
 <DollarSign className="h-4 w-4 text-green-600" />
 <span className="font-medium text-foreground">
 {formatCurrency(proposal.finalValue || proposal.proposedPrice)}
 </span>
 </div>
 {proposal.discount && proposal.discount > 0 && (
 <div className="text-sm text-muted-foreground">
 Desconto: {formatCurrency(proposal.discount)}
 {proposal.discountPercentage && (
 <span className="ml-1">({proposal.discountPercentage.toFixed(1)}%)</span>
 )}
 </div>
 )}
 {proposal.tradeVehicles && proposal.tradeVehicles.length > 0 && (
 <div className="text-xs text-muted-foreground space-y-0.5">
 <div className="font-medium">Troca:</div>
{proposal.tradeVehicles.map((tradeVehicle: any, index: number) => {
      const expensesTotal = tradeVehicle.expenses?.reduce(
        (sum: number, expense: any) => {
 const expenseValue = typeof expense.value === 'string'
 ? parseFloat(expense.value.replace(/[^\d,.-]/g, '').replace(',', '.')) || 0
 : Number(expense.value) || 0;
 return sum + expenseValue;
 },
 0
 ) || 0;
 const netValue = (tradeVehicle.offeredValue || 0) - expensesTotal;
 const vehicleName = tradeVehicle.brand && tradeVehicle.model
 ? `${tradeVehicle.brand.name} ${tradeVehicle.model.name} ${tradeVehicle.modelYear || ''}`.trim()
 : `Veículo ${index + 1}`;
 
 return (
 <div key={index} className="pl-2">
 {vehicleName}: {formatCurrency(netValue)}
 </div>
 );
 })}
 </div>
 )}
 {proposal.validUntil && (
 <div className="text-xs text-muted-foreground">
 Válido até: {new Date(proposal.validUntil).toLocaleDateString('pt-BR')}
 </div>
 )}
 </div>
 </TableCell>
 
 <TableCell>
 <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${getStatusBadgeClass(proposal.status)}`}>
 <span className={getStatusColor(proposal.status)}>
 {getStatusIcon(proposal.status)}
 </span>
 {getStatusLabel(proposal.status)}
 </span>
 </TableCell>
 
 <TableCell>
 {proposal.assignedUser ? (
 <div className="flex items-center">
 <User className="h-4 w-4 text-accent mr-2" />
 <span className="text-sm text-foreground">
 {proposal.assignedUser.name}
 </span>
 </div>
 ) : (
 <span className="text-sm text-muted-foreground">-</span>
 )}
 </TableCell>
 
 <TableCell>
 <div className="flex items-center">
 <Calendar className="h-4 w-4 text-muted-foreground mr-2" />
 <span className="text-sm text-muted-foreground">
 {new Date(proposal.createdAt).toLocaleDateString('pt-BR')}
 </span>
 </div>
 </TableCell>
 
 <TableCell>
 <div className="flex space-x-2">
 <button
 onClick={(e) => {
 e.stopPropagation()
 if (onViewProposal) {
 onViewProposal(proposal)
 } else {
 navigate(`/proposals/${proposal.id}`)
 }
 }}
 className="p-2 text-muted-foreground hover:text-accent dark:hover:text-accent"
 title="Visualizar"
 >
 <Eye className="h-4 w-4" />
 </button>
 {(() => {
 const nonEditableStatuses = ['accepted', 'approved', 'rejected', 'expired']
 const canEdit = !nonEditableStatuses.includes(proposal.status)
 
 if (!canEdit) return null
 
 return (
 <button
 onClick={(e) => {
 e.stopPropagation()
 onEditProposal(proposal)
 }}
 className="p-2 text-muted-foreground hover:text-accent dark:hover:text-accent"
 title="Editar"
 >
 <Edit className="h-4 w-4" />
 </button>
 )
 })()}
 <button
 onClick={(e) => {
 e.stopPropagation()
 onDeleteProposal(proposal.id)
 }}
 className="p-2 text-muted-foreground hover:text-red-600 dark:hover:text-red-400"
 title="Excluir"
 >
 <Trash2 className="h-4 w-4" />
 </button>
 </div>
 </TableCell>
 </TableRow>
 ))}
 </TableBody>
 </Table>
 )
}
